/**
 * #1186 (owner Q-297 b): which writes the api client may repeat.
 *
 * A request whose method is not GET/HEAD/OPTIONS is sent exactly once —
 * after a 5xx, a 408/429, or no response at all — UNLESS its route is in
 * `IDEMPOTENT_WRITE_ROUTES` below. One click must never create or send twice
 * (owner rule 09-30: no duplicate send): the person sees the server's error at
 * once and clicking again is their choice.
 *
 * This table is the ONLY thing that exempts a write. `isConsumeWrite` in
 * client.ts decides the consume timeout and the consume alert, never retry; a
 * new consume write is not repeatable until it has a row here.
 *
 * Every row carries the vingapi test (`serverProof`, `tests/….py::test_…`)
 * that proves a sequential replay of the same request is harmless. Client
 * retries are sequential: a retry starts only after its attempt failed.
 * `retryPolicy.guard.test.ts` refuses a row without a reason or a proof.
 */

export type WriteMethod = 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface IdempotentWriteRoute {
  method: WriteMethod;
  /** Matched against the request path (axios `config.url`, absolute or not). */
  route: RegExp;
  /** A URL the route matches; the guard and the I5 pin send to it. */
  example: string;
  /** Why a replay of the same request is harmless, in one sentence. */
  reason: string;
  /** The vingapi test proving it: `tests/<path>.py::test_<name>`. */
  serverProof: string;
}

export const IDEMPOTENT_WRITE_ROUTES: readonly IdempotentWriteRoute[] = [
  {
    method: 'PUT',
    route: /\/api\/view\/shares\/[^/?#]+\/?(?:[?#].*)?$/,
    example: '/api/view/shares/stub-1186/',
    reason:
      'The consume state PUT carries an absolute per-component snapshot, so a replay of the same body replaces the same answers instead of accumulating rows.',
    serverProof:
      'tests/consume/assignment/pipeline/test_reported_928.py::test_consume_put_retry_relies_on_put_being_idempotent_for_same_body',
  },
  {
    method: 'POST',
    route: /\/api\/view\/shares\/[^/?#]+\/files\/accept\/?(?:[?#].*)?$/,
    example: '/api/view/shares/stub-1186/files/accept/',
    reason:
      'File accept dedups on the upload etag, so the same body sent again returns the same file row with a fresh claim and no second dispatch.',
    // The SEQUENTIAL proof (PM 2026-10-02). Two identical accepts at once give
    // one 200 and one 409 (vingapi#1764, open server defect); switch this to
    // `::test_accept_same_body_concurrent_one_file_row` when that fix lands.
    serverProof:
      'tests/consume/shared/test_accept_endpoint.py::test_accept_same_body_twice_returns_same_file_one_row_no_second_dispatch',
  },
];

const READ_METHODS: ReadonlySet<string> = new Set(['GET', 'HEAD', 'OPTIONS']);

/** GET/HEAD/OPTIONS. A missing method is axios's default, GET. */
export function isReadMethod(method: string | undefined): boolean {
  return READ_METHODS.has((method || 'GET').toUpperCase());
}

/** The table row that lets this write be repeated, or undefined (sent once). */
export function idempotentWriteRoute(
  method: string,
  url: string,
  table: readonly IdempotentWriteRoute[] = IDEMPOTENT_WRITE_ROUTES,
): IdempotentWriteRoute | undefined {
  const verb = method.toUpperCase();
  return table.find((row) => row.method === verb && row.route.test(url));
}

/**
 * #1336 (1336-b): the server's own "nothing was written, send it again" answer.
 *
 * vingapi answers 409 `{"detail": {"error": "account_merged_retry"}}` when the
 * request lost a race with a direct merge of the caller's login, and ONLY on a
 * route it has proved commits and sends nothing before that failure
 * (`DIRECT_MERGE_RETRY_ROUTES`, vingapi#1858 rev 6). The client resends the same
 * request ONCE, and only on a route in the table below: the code alone never
 * repeats a write. A route not in the table (or a request of the wrong shape)
 * gets the 409 as a generic server error.
 *
 * Every route row carries the vingapi test that proves its failing request
 * committed nothing on any session and sent nothing; every code row carries the
 * guard that makes the server's allowlist and its proofs agree.
 * `retryPolicy.guard.test.ts` refuses a row without them.
 */

/** What a route row may read of the request: the parsed JSON body and whether it carries a token. */
export interface RetryRequestShape {
  body: Record<string, unknown> | undefined;
  authenticated: boolean;
}

export interface ServerRetryRoute {
  method: WriteMethod;
  route: RegExp;
  /** A URL the route matches. */
  example: string;
  /**
   * The phase / auth shape this row covers, when the route is only proved for
   * part of its traffic. `accepts` must match `matches`, `refuses` must not.
   */
  shape?: {
    describe: string;
    matches: (request: RetryRequestShape) => boolean;
    accepts: RetryRequestShape;
    refuses: RetryRequestShape;
  };
  /** The vingapi test proving the failing request wrote and sent nothing. */
  serverProof: string;
}

export interface ServerRetryCode {
  /** Resends allowed per request. */
  times: number;
  routes: readonly ServerRetryRoute[];
  /** The vingapi guard tying the server allowlist to its proofs. */
  serverProof: string;
}

// The vingapi paths are the S2-DM slice's; the test names are from the 1858 plan (rev 6).
const MERGE_TESTS = 'tests/people/matching/merge/test_1858_s_dm2.py';

export const ACCOUNT_MERGED_RETRY = 'account_merged_retry';

export const SERVER_RETRY_CODES: Readonly<Record<string, ServerRetryCode>> = {
  [ACCOUNT_MERGED_RETRY]: {
    times: 1,
    serverProof: `${MERGE_TESTS}::test_account_merged_retry_routes_each_have_proof_test`,
    routes: [
      {
        method: 'PUT',
        route: /\/api\/view\/shares\/[^/?#]+\/?(?:[?#].*)?$/,
        example: '/api/view/shares/stub-1336/',
        serverProof: `${MERGE_TESTS}::test_direct_merge_concurrent_answer_save_by_folded_login_retryable`,
      },
      {
        method: 'POST',
        route: /\/api\/auth\/login\/(?:[?#].*)?$/,
        example: '/api/auth/login/',
        serverProof: `${MERGE_TESTS}::test_direct_merge_concurrent_signin_of_folded_login_signs_in_survivor`,
      },
      {
        method: 'POST',
        route: /\/api\/auth\/verify\/(?:[?#].*)?$/,
        example: '/api/auth/verify/',
        shape: {
          describe: 'logged-in caller, no verification_key (verify-with-key is never retried: its key names the deleted login)',
          matches: ({ body, authenticated }) => authenticated && !(body && 'verification_key' in body),
          accepts: { body: { code: '123456' }, authenticated: true },
          refuses: { body: { code: '123456', verification_key: 'k' }, authenticated: false },
        },
        serverProof: `${MERGE_TESTS}::test_account_merged_retry_verify_commits_and_sends_nothing`,
      },
      {
        method: 'POST',
        route: /\/api\/auth\/link\/exchange\/(?:[?#].*)?$/,
        example: '/api/auth/link/exchange/',
        shape: {
          describe: 'the complete phase only (the stage phase is not retried)',
          matches: ({ body }) => body?.phase === 'complete',
          accepts: { body: { phase: 'complete' }, authenticated: false },
          refuses: { body: { phase: 'stage' }, authenticated: false },
        },
        serverProof: `${MERGE_TESTS}::test_account_merged_retry_link_exchange_commits_and_sends_nothing`,
      },
    ],
  },
};

/** The request body as an object: axios hands the interceptor a JSON string after transformRequest. */
export function parseRetryBody(data: unknown): Record<string, unknown> | undefined {
  try {
    const value: unknown = typeof data === 'string' ? JSON.parse(data) : data;
    return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : undefined;
  } catch {
    return undefined;
  }
}

/** The route row that lets this request be resent once after `code`, or undefined. */
export function serverRetryRoute(
  code: string,
  method: string,
  url: string,
  request: RetryRequestShape,
  table: Readonly<Record<string, ServerRetryCode>> = SERVER_RETRY_CODES,
): ServerRetryRoute | undefined {
  const verb = method.toUpperCase();
  return table[code]?.routes.find(
    (row) => row.method === verb && row.route.test(url) && (!row.shape || row.shape.matches(request)),
  );
}
